import { useEffect, useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { normalizeLocation } from '../../lib/invitation';
import { cn } from '../../lib/utils';

function MapResize({ trigger }) {
  const map = useMap();
  useEffect(() => {
    const id = window.setTimeout(() => map.invalidateSize(), 80);
    return () => window.clearTimeout(id);
  }, [map, trigger]);
  return null;
}

function MapBounds({ markers, selectedId }) {
  const map = useMap();
  useEffect(() => {
    if (!markers.length) return undefined;

    if (markers.length === 1) {
      map.setView([markers[0].lat, markers[0].lng], Math.max(map.getZoom() || 15, 14), {
        animate: false,
      });
      return undefined;
    }

    const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
    map.fitBounds(bounds.pad(0.2), { animate: false, maxZoom: 16 });
    return undefined;
  }, [map, markers, selectedId]);
  return null;
}

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick?.({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

export function createInvitationMarkerIcon(markerUrl, accentColor = '#F68B1F') {
  if (markerUrl) {
    return L.icon({
      iconUrl: markerUrl,
      iconSize: [40, 48],
      iconAnchor: [20, 48],
      popupAnchor: [0, -40],
      className: 'ba-map-custom-marker',
    });
  }

  const svg = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
      <path fill="${accentColor}" stroke="#fff" stroke-width="2" d="M14 1C7.4 1 2 6.4 2 13c0 8.4 12 25 12 25s12-16.6 12-25C26 6.4 20.6 1 14 1z"/>
      <circle cx="14" cy="13" r="5" fill="#fff"/>
    </svg>`
  );

  return L.icon({
    iconUrl: `data:image/svg+xml;charset=UTF-8,${svg}`,
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -36],
  });
}

function InvitationMap({
  location,
  accentColor = '#F68B1F',
  className,
  heightClass = 'h-44',
  interactive = false,
  onMapClick,
  onMarkerClick,
  selectedMarkerId,
  resizeTrigger,
}) {
  const loc = normalizeLocation(location);
  const markers = loc.markers;
  const defaultIconUrl = loc.markerUrl || '';

  const iconsByKey = useMemo(() => {
    const cache = new Map();
    markers.forEach((marker) => {
      const url = marker.markerUrl || defaultIconUrl || '';
      const key = url || `__default__:${accentColor}`;
      if (!cache.has(key)) {
        cache.set(key, createInvitationMarkerIcon(url || null, accentColor));
      }
    });
    return cache;
  }, [markers, defaultIconUrl, accentColor]);

  if (loc.showMap === false || markers.length === 0) return null;

  const center = [markers[0].lat, markers[0].lng];
  const boundsKey = markers.map((m) => `${m.id}:${m.lat}:${m.lng}`).join('|');

  return (
    <div className={cn('ba-invitation-map overflow-hidden rounded-xl border border-[var(--ba-surface-border)]', className)}>
      <MapContainer
        center={center}
        zoom={15}
        scrollWheelZoom={interactive}
        dragging={interactive}
        doubleClickZoom={interactive}
        zoomControl={interactive}
        attributionControl
        className={cn('z-0 w-full', heightClass)}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {markers.map((marker) => {
          const url = marker.markerUrl || defaultIconUrl || '';
          const key = url || `__default__:${accentColor}`;
          const icon = iconsByKey.get(key);
          const label = (marker.name || '').trim();
          return (
            <Marker
              key={marker.id}
              position={[marker.lat, marker.lng]}
              icon={icon}
              eventHandlers={
                onMarkerClick
                  ? { click: (e) => { e.originalEvent?.stopPropagation?.(); onMarkerClick(marker.id); } }
                  : undefined
              }
              opacity={selectedMarkerId && selectedMarkerId !== marker.id ? 0.72 : 1}
            >
              {label ? (
                <Tooltip
                  direction="top"
                  offset={[0, -36]}
                  opacity={1}
                  permanent
                  className="ba-map-marker-label"
                >
                  {label}
                </Tooltip>
              ) : null}
            </Marker>
          );
        })}
        <MapBounds markers={markers} selectedId={selectedMarkerId} />
        <MapResize trigger={`${boundsKey},${resizeTrigger || ''}`} />
        {onMapClick && <MapClickHandler onMapClick={onMapClick} />}
      </MapContainer>
    </div>
  );
}

export default InvitationMap;
